import { useMemo } from 'react';
import { CLIENT_NOTES_CHANGED_EVENT, readClientNotes, updateClientNotes, useClientNotes } from './clientNotesStore';
import type { ClientNotes } from './clientNotesStore';

/**
 * 候選人評估 — the five recruiter-scored angles behind the 招募 radar, and the
 * 總分 read off them. Shared by RecruitRadar (which edits them) and 招募 IDEA
 * (which only reads them), so both say the same number about one person.
 *
 * Stored on the server as `factsheet.recruitment.scores`, through the notes
 * store (./clientNotesStore) — the summary below does not change.
 *
 * The total is a reference for the recruiter, not a verdict: equal weights,
 * bands deliberately coarse, and no total at all until every angle is rated —
 * two fives and three blanks is not a 40-point candidate.
 *
 * The total is shown out of 100 (the five 1–5 scores summed, ×4): 「28 分」
 * reads at a glance where 「7/25」 needs arithmetic. So it runs 20–100.
 */

export const RECRUIT_AXES = ['motive', 'network', 'comms', 'discipline', 'family'] as const;
export type RecruitAxis = (typeof RECRUIT_AXES)[number];
export type RecruitScores = Partial<Record<RecruitAxis, number>>;

export const MAX_RECRUIT_SCORE = 5;
const RAW_MAX = MAX_RECRUIT_SCORE * RECRUIT_AXES.length;
export const MAX_RECRUIT_TOTAL = 100;

export type RecruitTier = 'priority' | 'nurture' | 'hold';

// 80–100 優先跟進 · 56–76 值得培養 · ≤52 暫緩 (the old 20 / 14 of 25)
const TIER_FLOORS: Array<[number, RecruitTier]> = [
  [80, 'priority'],
  [56, 'nurture']
];

// 家人反對 is the concern a candidate cannot settle alone (招募 IDEA's flagged
// pair), so a low family score is called out however good the total looks.
const FAMILY_CONCERN_AT_OR_BELOW = 2;

export type RecruitSummary = {
  rated: number;
  total: number | null;
  tier: RecruitTier | null;
  familyConcern: boolean;
};

export const summariseRecruitScores = (scores: RecruitScores): RecruitSummary => {
  const values = RECRUIT_AXES.map(axis => scores[axis]).filter((v): v is number => typeof v === 'number');
  const complete = values.length === RECRUIT_AXES.length;
  const total = complete ? Math.round((values.reduce((sum, v) => sum + v, 0) / RAW_MAX) * MAX_RECRUIT_TOTAL) : null;
  const tier = total === null ? null : TIER_FLOORS.find(([floor]) => total >= floor)?.[1] || 'hold';
  const family = scores.family;
  return {
    rated: values.length,
    total,
    tier,
    familyConcern: typeof family === 'number' && family <= FAMILY_CONCERN_AT_OR_BELOW
  };
};

const scoresOf = (notes: ClientNotes | null): RecruitScores => {
  const raw = (notes && notes.recruitment.scores) || {};
  const scores: RecruitScores = {};
  RECRUIT_AXES.forEach(axis => {
    const value = raw[axis];
    if (typeof value === 'number' && value >= 1 && value <= MAX_RECRUIT_SCORE) scores[axis] = value;
  });
  return scores;
};

export const readRecruitScores = (clientId: string): RecruitScores => scoresOf(readClientNotes(clientId));

// Kept for the list-level reader (./recruitListView), now the notes store's event.
export const RECRUIT_SCORES_CHANGED_EVENT = CLIENT_NOTES_CHANGED_EVENT;

/** All five axes are sent: a rated axis as its score, an unrated one as null (cleared). */
export const writeRecruitScores = (clientId: string, scores: RecruitScores) => {
  const body = RECRUIT_AXES.reduce((acc, axis) => {
    acc[axis] = typeof scores[axis] === 'number' ? (scores[axis] as number) : null;
    return acc;
  }, {} as Record<RecruitAxis, number | null>);
  void updateClientNotes(clientId, { recruitment: { scores: body } }, notes => ({
    ...notes,
    recruitment: { ...notes.recruitment, scores: scoresOf({ ...notes, recruitment: { scores } }) }
  }));
};

/** One client's scores, kept current across every component showing them. */
export const useRecruitScores = (clientId: string | null | undefined) => {
  const notes = useClientNotes(clientId);
  return useMemo(() => scoresOf(notes), [notes]);
};
